import axios from 'axios'
import { API_URL } from '../utils/ApiLinks'
import {
  ChannelDto,
  UpdateChannelNameDto,
  UpdateChannelDescriptionDto,
  UpdateChannelPrivacyDto,
  UpdateChannelRequiresInviteDto,
} from '../types/channel'
import {UpdateChannelMemberRoleDto} from '../types/channelMember'
import { CreateChannelInviteDto } from '../types/channelInvite'
import { CreateGlobalMessageDto, UpdateGlobalMessageContentDto } from '../types/globalMessage'
import { CreateChannelMessageMentionDto } from '../types/channelMessageMention'
import { JoinChannelDto, LeaveChannelDto } from '../types/joinLeaveChannel'

export const ChannelService = {
  // ──────────────── CRUD Canales ────────────────

  createChannel: async (data: FormData) => {
    const res = await axios.post(`${API_URL}/channel`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if(res.status !== 200) {
      throw new Error("Error al crear el canal");
    }
    return {success : true, data: res.data as ChannelDto};
  },

  getPublicChannels: async () => {
    const res = await axios.get(`${API_URL}/channel/get-public-channels`)
    return res.data as ChannelDto[]
  },

  getChannel: async (id: number) => {
    
    try {
      const res = await axios.get(`${API_URL}/channel/${id}`)
      if(res.status !== 200) {
        throw new Error("Error al obtener el canal");
      }
      return {success: true, data: res.data as ChannelDto};
    } catch (error) {
      console.error("Error al obtener el canal:", error)
      throw new Error("Error al obtener el canal");
    }
  },

  deleteChannel: async (id: number) => {
    try {
      await axios.delete(`${API_URL}/channel/${id}`)
    } catch (error) {
      console.error("Error al eliminar el canal:", error)
      throw new Error("Error al eliminar el canal");
    }
  },

  getChannelsForProfile: async (profileId: number) => {
    const res = await axios.get(`${API_URL}/channel/profile/${profileId}`)
    return res.data as ChannelDto[]
  },

  searchChannels: async (query: string, profileId: number) => {
    const res = await axios.get(`${API_URL}/channel/search`, {
      params: {
        query,
        profileId,
      },
    })
    return res.data as ChannelDto[]
  },

  uploadChatsFiles: async (files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file)
    })

    try {
      const res = await axios.post(`${API_URL}/channel/upload-chats-files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if(res.status === 200){
        return { success: res.data.success, message: res.data.message, data: res.data.data };
      }
    } catch (error) {
      console.error("Error al subir archivos de chat:", error)
      throw new Error("Error al subir archivos de chat")
    }
  },

  // ──────────────── Actualizaciones ────────────────

  updateChannelName: async (dto: UpdateChannelNameDto) => {
    const formData = new FormData()
    formData.append('channelId', dto.channelId.toString())
    formData.append('name', dto.name)
    const res = await axios.post(`${API_URL}/channel/${dto.channelId}/name`, formData)
    return res.data
  },

  updateChannelImage: async (channelId: number, imageFile: File) => {
    try {
      const formData = new FormData()
      formData.append('ImageUrl', imageFile)
      const res = await axios.put(`${API_URL}/channel/${channelId}/update-image`, formData)
      if(res.status === 200){
        return { success: res.data.success, message: res.data.message, data: res.data.data };
      }
    } catch (error : any) {
      console.error("Error al actualizar la imagen del canal:", error.response)
      throw new Error(error.response?.data.message || "Error al actualizar la imagen del canal");
    }
  },

  updateChannelDescription: async (dto: UpdateChannelDescriptionDto) => {
    const formData = new FormData()
    formData.append('channelId', dto.channelId.toString())
    formData.append('description', dto.description)
    const res = await axios.post(`${API_URL}/channel/${dto.channelId}/description`, formData)
    return res.data
  },

  updateWhatsappLink: async (dto: { channelId: number, whatsappLink: string }) => {
    const formData = new FormData()
    formData.append('WhatsappLink', dto.whatsappLink)
    const res = await axios.put(`${API_URL}/channel/${dto.channelId}/whatsapp-link`, formData)
    return res.data.data
  },

  updateChannelPrivacy: async (dto: UpdateChannelPrivacyDto) => {
    const formData = new FormData()
    formData.append('channelId', dto.channelId.toString())
    formData.append('isPublic', dto.isPublic.toString())
    const res = await axios.post(`${API_URL}/channel/${dto.channelId}/privacy`, formData)
    return res.data
  },

  updateRequiresInvite: async (dto: UpdateChannelRequiresInviteDto) => {
    const formData = new FormData()
    formData.append('channelId', dto.channelId.toString())
    formData.append('requiresInvite', dto.requiresInvite.toString())
    const res = await axios.post(`${API_URL}/channel/${dto.channelId}/requires-invite`, formData)
    return res.data
  },

  // ──────────────── Miembros ────────────────

  updateMemberRole: async (dto: UpdateChannelMemberRoleDto) => {
    try {
      console.log("Updating member role:", {
        channelId: dto.channelId,
        channelMemberId: dto.channelMemberId,
        role: dto.role,
      });
      const res = await axios.post(`${API_URL}/channel/member/${dto.profileId}/role`, {
        channelId: dto.channelId,
        channelMemberId: dto.channelMemberId,
        role: dto.role,
      });
      if (res.data.success === true) {
        return { success: true, message: res.data.message, data: res.data.data };
      }
      
    } catch (error: any) {
      console.error("Error al actualizar el rol del miembro:", error)
      throw new Error(error.response?.data.message || "Error al actualizar el rol del miembro");
    }
  },

  removeMember: async (channelId: number, profileId: number) => {
    try {
      const response = await axios.delete(`${API_URL}/channel/${channelId}/member/${profileId}`)
      if (response.status === 200) {
        return { success: response.data.success, message: response.data.message, data: response.data.data };
      }
    } catch (error: any) {
      console.error("Error al eliminar el miembro del canal:", error)
      throw new Error(error.response?.data.message || "Error al eliminar el miembro del canal");
      
    }
  },

  getChannelMembers: async (channelId: number, query?: string) => {
    const res = await axios.get(`${API_URL}/channel/${channelId}/members`, { params: { query } })
    return res.data
  },

  // ──────────────── Invitaciones ────────────────

  getPendingInvites: async (profileId: number) => {
    const res = await axios.get(`${API_URL}/channel/invites/${profileId}`)
    return res.data
  },

  inviteToChannel: async (channelId: number, dto: CreateChannelInviteDto) => {
    try {
      const res = await axios.post(`${API_URL}/channel/${channelId}/invite`, dto);
      if(res.status !== 200) {
        throw new Error("Error al invitar al canal");
      }
      return { success: true, message: "Invitación enviada exitosamente.", data: res.data };
    } catch (error: any) {
      // Manejo de errores personalizados
      console.error("Error al invitar al canal:", error.response?.data);
      throw new Error(error.response?.data || "Error al invitar al canal");
    }
  },

  respondToInvite: async (inviteId: number, dto: { response: "Accepted" | "Rejected" }) => {
    const res = await axios.post(`${API_URL}/channel/invites/${inviteId}/respond`, dto)
    return res.data
  },

  // ──────────────── Unión y salida ────────────────

  joinPublicChannel: async (channelId: number, dto: JoinChannelDto) => {
    const res = await axios.post(`${API_URL}/channel/${channelId}/join`, dto)
    if(res.status !== 200) {
      throw new Error("Error al unirse al canal");
    }
    return {success : true, message: "Unido al canal exitosamente.",  data: res.data};
  },

  leaveChannel: async (channelId: number, dto: LeaveChannelDto) => {
    try {
      const res = await axios.post(`${API_URL}/channel/${channelId}/leave`, dto)
      if(res.status === 200) {
        return { success: true, message: res.data.message, data: res.data.data };
      }
      
    } catch (error: any) {
      console.error("Error al salir del canal:", error)
      throw new Error(error.response?.data.message || "Error al salir del canal");
    }
  },

  // ──────────────── Mensajes Globales ────────────────

  getGlobalMessages: async () => {
    const res = await axios.get(`${API_URL}/channel/global-messages`)
    return res.data
  },

  createGlobalMessage: async (dto: CreateGlobalMessageDto) => {
    const res = await axios.post(`${API_URL}/channel/global-messages`, dto)
    return res.data
  },

  updateGlobalMessageContent: async (dto: UpdateGlobalMessageContentDto) => {
    const formData = new FormData()
    formData.append('messageId', dto.messageId.toString())
    formData.append('content', dto.content)
    const res = await axios.post(`${API_URL}/channel/global-messages/${dto.messageId}/content`, formData)
    return res.data
  },

  // ──────────────── Menciones ────────────────

  mentionUserInMessage: async (dto: CreateChannelMessageMentionDto) => {
    const res = await axios.post(`${API_URL}/channel/messages/mentions`, dto)
    return res.data
  },
  
  getChannelMessages: async (channelId: number) => {
  const res = await axios.get(`${API_URL}/channel/channel-messages/${channelId}`)
  return res.data
},

  getActiveUserProfiles: async () => {
    const res = await axios.get(`${API_URL}/channel/get-global-active-users`)
    return res.data
  },

}

